import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { boolean, object, string } from 'yup';

import { tCommon, tOrder } from '@frontend/locales/t.generated';
import { AllowedOrdersSortField } from '@frontend/modules/order/order-sort.model';
import {
  AllowedAudioTuneTypeValues,
  AllowedOrderCardSizeVariantTypeValues,
  usePersistentUiSettings,
} from '@frontend/utils';

import { DialogForm } from '../dialog-form.component';
import { OrderListSettingsForm } from './order-list-settings-form.component';
import { OrderListSettingsFormInput } from './order-list-settings-form-input.interface';

export interface OrderListSettingsDialogProps {
  openState: boolean;
  toggleDialog: (open: boolean) => void;
}

export const OrderListSettingsDialog = ({
  openState,
  toggleDialog,
}: OrderListSettingsDialogProps) => {
  const {
    ordersPage: { orderCardSettings, audio, sortField },
    updateOrdersPage,
  } = usePersistentUiSettings();

  const handleSubmit = (input: OrderListSettingsFormInput) => {
    updateOrdersPage({
      orderCardSettings: {
        showItems: input.showItems,
        showGuestName: input.showGuestName,
        showTimeParameters: input.showTimeParameters,
        itemFontSize: input.itemFontSize,
        cardSize: input.cardSize,
      },
      audio: { ...audio, tune: input.tune },
      sortField: input.sortField,
    });
    toggleDialog(false);
  };

  const validationSchema = useMemo(
    () =>
      object<OrderListSettingsFormInput>({
        showItems: boolean(),
        showGuestName: boolean(),
        showTimeParameters: boolean(),
        itemFontSize: string()
          .required()
          .oneOf(AllowedOrderCardSizeVariantTypeValues),
        cardSize: string()
          .required()
          .oneOf(AllowedOrderCardSizeVariantTypeValues),
        sortField: string().required().oneOf(AllowedOrdersSortField),
        tune: string().required().oneOf(AllowedAudioTuneTypeValues),
      }),
    [],
  );

  return (
    <Dialog
      open={openState}
      onClose={() => {
        toggleDialog(false);
      }}
      slotProps={{ paper: { sx: { width: 720, maxWidth: '90vw' } } }}
    >
      {openState && (
        <Formik<OrderListSettingsFormInput>
          initialValues={{
            showItems: orderCardSettings.showItems,
            showGuestName: orderCardSettings.showGuestName,
            showTimeParameters: orderCardSettings.showTimeParameters,
            itemFontSize: orderCardSettings.itemFontSize,
            cardSize: orderCardSettings.cardSize,
            sortField,
            tune: audio.tune,
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {(formikProps) => {
            return (
              <DialogForm>
                <DialogTitle>{tOrder.headers.settings()}</DialogTitle>

                <DialogContent>
                  <OrderListSettingsForm />
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      toggleDialog(false);
                    }}
                    variant="outlined"
                    color="neutral"
                    sx={{ minWidth: 128 }}
                  >
                    {tCommon.actions.back()}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    type="submit"
                    sx={{ minWidth: 128 }}
                    disabled={!formikProps.isValid || !formikProps.dirty}
                  >
                    {tCommon.actions.save()}
                  </Button>
                </DialogActions>
              </DialogForm>
            );
          }}
        </Formik>
      )}
    </Dialog>
  );
};
